import { Alert } from '../primitives/alert.js'
import { Button } from '../primitives/button.js'
import { IconButton } from '../primitives/icon-button.js'
import { Snackbar } from '../primitives/snackbar.js'
import { Copy as CopyIcon } from '@tunasong/icons'
import type { FC } from 'react'
import { useCallback, useState } from 'react'

export interface CopyToClipboardProps {
  text?: string
  variant?: 'button' | 'icon'
  size?: 'small' | 'large' | undefined
  title?: string
  snackBarText?: string
}

export const CopyToClipboard: FC<CopyToClipboardProps> = props => {
  const {
    text = '',
    variant = 'button',
    snackBarText = `Copied to clipboard`,
    size,
    title = 'Copy to clipboard',
  } = props
  const [showSnackbar, setShowSnackbar] = useState(false)

  const hideSnackbar = useCallback(() => setShowSnackbar(false), [])

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      el.setSelectionRange(0, 99999)
      document.execCommand('copy')
      document.body.removeChild(el)
    }

    setShowSnackbar(true)
  }, [text])

  if (!text) {
    return null
  }

  return (
    <>
      {variant === 'button' && (
        <Button size={size} onClick={copyToClipboard} title={title}>
          {title}
        </Button>
      )}
      {variant === 'icon' && (
        <IconButton color="inherit" onClick={copyToClipboard} title={title} aria-label={title}>
          <CopyIcon fontSize={size} />
        </IconButton>
      )}

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={showSnackbar}
        autoHideDuration={100000}
        onClose={hideSnackbar}
      >
        <Alert severity="success">{snackBarText}</Alert>
      </Snackbar>
    </>
  )
}
